import type { EntityType, Interaction, Person, Segment, Trip } from "../types";
import { normalizeEntityType } from "../types";
import type { EntityInfo } from "../hooks/useEntityMap";
import {
  ACTIVITY_ROLE_TYPES,
  SEGMENT_ROLE_TYPES,
  TRIP_ROLE_TYPES,
  getRoleLabel,
  type RoleTypeOption,
} from "../config/peopleRoleTypes";
import { stripRichText } from "../components/common/richText";
import {
  activityStart,
  activityZone,
  entityTypeIcon,
  entityTypeLabel,
  formatEventType,
  segmentDayLookup,
  segmentZoneLookup,
  type Zone,
} from "./tripDisplay";
import { dateFromDayKey, dayKeyOf } from "./calendarDay";

/**
 * Search across every trip: the people met, the notes written, and the places
 * visited, the way searching the old Keep notes worked.
 *
 * Firestore has no text search, and one family's trips are small (a few
 * thousand activities), so the app reads the trips the user belongs to and
 * matches in memory. Each trip, segment and activity becomes one `SearchDoc`
 * holding every bit of text a person might look for; a query matches a doc
 * when every word of it appears somewhere in that doc. Matching ignores case
 * and accents, so "cafe" finds "Café".
 */

/** What kind of text a match was in, for the label beside the snippet. */
export type SearchFieldKind = "name" | "place" | "person" | "party" | "note" | "order" | "answer";

export interface SearchText {
  kind: SearchFieldKind;
  /** Shown beside the snippet: "Notes", "Tour Guide", "Order". */
  label: string;
  text: string;
}

export type SearchDocKind = "trip" | "segment" | "activity";

export interface SearchDoc {
  kind: SearchDocKind;
  id: string;
  tripId: string;
  title: string;
  icon: string;
  /** The stretch of the trip it's in, for an activity. */
  context: string | null;
  /** When it happened, for sorting and the date on the row. */
  when: Date | null;
  zone: Zone;
  href: string;
  texts: SearchText[];
}

/** A run of characters in a text that matched a query word. */
export interface MatchRange {
  start: number;
  end: number;
}

export interface SearchMatch extends SearchText {
  ranges: MatchRange[];
}

export interface SearchHit {
  doc: SearchDoc;
  /** The doc's texts that matched, in the doc's order. */
  matches: SearchMatch[];
}

export interface TripSearchGroup {
  tripId: string;
  hits: SearchHit[];
}

// ---------- Normalizing ----------

/**
 * Lower-cases a string and drops accents, keeping a map from each character
 * of the result back to its position in the original, so a match found in the
 * folded text can be highlighted in the text as written.
 */
export function foldWithMap(text: string): { folded: string; map: number[] } {
  let folded = "";
  const map: number[] = [];
  let i = 0;
  for (const ch of text) {
    const f = ch.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
    for (let k = 0; k < f.length; k++) map.push(i);
    folded += f;
    i += ch.length;
  }
  map.push(text.length);
  return { folded, map };
}

export function fold(text: string): string {
  return foldWithMap(text).folded;
}

/** The words of a query, folded. Quoted runs stay together: `"jungle cruise"`. */
export function queryTerms(query: string): string[] {
  const terms: string[] = [];
  const re = /"([^"]+)"|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(query))) {
    const t = fold((m[1] ?? m[2] ?? "").trim());
    if (t) terms.push(t);
  }
  // A longer term covering a shorter one makes the shorter one redundant, and
  // a repeated word adds nothing.
  return [...new Set(terms)];
}

function rangesOf(text: string, terms: string[]): MatchRange[] {
  const { folded, map } = foldWithMap(text);
  const ranges: MatchRange[] = [];
  for (const term of terms) {
    let from = 0;
    for (;;) {
      const at = folded.indexOf(term, from);
      if (at < 0) break;
      ranges.push({ start: map[at]!, end: map[at + term.length]! });
      from = at + term.length;
    }
  }
  return mergeRanges(ranges);
}

function mergeRanges(ranges: MatchRange[]): MatchRange[] {
  const sorted = ranges.slice().sort((a, b) => a.start - b.start);
  const out: MatchRange[] = [];
  for (const r of sorted) {
    const last = out[out.length - 1];
    if (last && r.start <= last.end) last.end = Math.max(last.end, r.end);
    else out.push({ ...r });
  }
  return out;
}

// ---------- Matching ----------

/** A doc's hit for the query, or null when some word of it is missing. */
export function matchDoc(doc: SearchDoc, terms: string[]): SearchHit | null {
  if (terms.length === 0) return null;
  const foldedTexts = doc.texts.map((t) => fold(t.text));
  for (const term of terms) {
    if (!foldedTexts.some((f) => f.includes(term))) return null;
  }
  const matches: SearchMatch[] = [];
  doc.texts.forEach((t, i) => {
    if (!terms.some((term) => foldedTexts[i]!.includes(term))) return;
    matches.push({ ...t, ranges: rangesOf(t.text, terms) });
  });
  return { doc, matches };
}

const KIND_ORDER: Record<SearchDocKind, number> = { trip: 0, segment: 1, activity: 2 };

/**
 * Every doc matching the query, grouped by trip. Trips keep the order they're
 * given in (newest first, as the trip list has them); inside a trip the trip
 * itself comes first, then its segments and activities in the order they
 * happened.
 */
export function searchTrips(
  docs: SearchDoc[],
  query: string,
  tripOrder: string[],
): TripSearchGroup[] {
  const terms = queryTerms(query);
  if (terms.length === 0) return [];
  const byTrip = new Map<string, SearchHit[]>();
  for (const doc of docs) {
    const hit = matchDoc(doc, terms);
    if (!hit) continue;
    const list = byTrip.get(doc.tripId) ?? [];
    list.push(hit);
    byTrip.set(doc.tripId, list);
  }
  const groups: TripSearchGroup[] = [];
  for (const tripId of tripOrder) {
    const hits = byTrip.get(tripId);
    if (!hits) continue;
    hits.sort((a, b) => {
      const k = KIND_ORDER[a.doc.kind] - KIND_ORDER[b.doc.kind];
      if (k !== 0 && (a.doc.kind === "trip" || b.doc.kind === "trip")) return k;
      const ta = a.doc.when?.getTime() ?? Number.POSITIVE_INFINITY;
      const tb = b.doc.when?.getTime() ?? Number.POSITIVE_INFINITY;
      return ta - tb || k;
    });
    groups.push({ tripId, hits });
  }
  return groups;
}

/** A piece of a snippet: plain text, or a run that matched. */
export interface SnippetPart {
  text: string;
  hit: boolean;
}

/**
 * The part of a text worth showing for a match: the whole thing when it's
 * short, else a window around the first match with ellipses, so a match deep
 * in a long note still shows up on the row.
 */
export function snippetParts(text: string, ranges: MatchRange[], radius = 60): SnippetPart[] {
  let from = 0;
  let to = text.length;
  const first = ranges[0];
  if (first && text.length > radius * 2 + 20) {
    from = Math.max(0, first.start - radius);
    to = Math.min(text.length, first.end + radius * 2);
    // Start and end on a word boundary when one is near.
    if (from > 0) {
      const space = text.indexOf(" ", from);
      if (space >= 0 && space < first.start) from = space + 1;
    }
    if (to < text.length) {
      const space = text.lastIndexOf(" ", to);
      if (space > first.end) to = space;
    }
  }
  const parts: SnippetPart[] = [];
  if (from > 0) parts.push({ text: "…", hit: false });
  let pos = from;
  for (const r of ranges) {
    if (r.end <= from || r.start >= to) continue;
    const s = Math.max(r.start, from);
    const e = Math.min(r.end, to);
    if (s > pos) parts.push({ text: text.slice(pos, s), hit: false });
    parts.push({ text: text.slice(s, e), hit: true });
    pos = e;
  }
  if (pos < to) parts.push({ text: text.slice(pos, to), hit: false });
  if (to < text.length) parts.push({ text: "…", hit: false });
  return parts;
}

// ---------- Building the docs ----------

export interface TripLike extends Pick<Trip, "name" | "notes" | "travelParty" | "people" | "startDate"> {
  id: string;
  timezone?: string | null;
}

export interface SegmentLike
  extends Pick<Segment, "name" | "notes" | "people" | "entityId" | "startDate" | "parentSegmentId"> {
  id: string;
  timezone?: string | null;
  portName?: string | null;
  dayNumber?: number | null;
}

export type InteractionLike = Pick<
  Interaction,
  "entityId" | "name" | "notes" | "people" | "events" | "customResponses" | "segmentId"
> & {
  id: string;
  timezone?: string | null;
  createdAt?: Interaction["createdAt"];
};

export interface TripCorpus {
  segments: SegmentLike[];
  interactions: InteractionLike[];
}

function addText(texts: SearchText[], kind: SearchFieldKind, label: string, raw: unknown) {
  if (typeof raw !== "string" && typeof raw !== "number") return;
  const text = typeof raw === "string" ? stripRichText(raw) : String(raw);
  if (text) texts.push({ kind, label, text });
}

function addPeople(texts: SearchText[], people: Person[] | null | undefined, roles: RoleTypeOption[]) {
  for (const p of people ?? []) {
    if (!p?.name) continue;
    addText(texts, "person", p.role ? getRoleLabel(p.role, roles) : "Person", p.name);
  }
}

/** Keys of `customResponses` that hold app state rather than something written down. */
const NON_TEXT_RESPONSE_KEYS = new Set([
  "activityType",
  "entityType",
  "hasTrivia",
  "orders",
  "rounds",
  "questions",
  "score",
  "winningScore",
]);

/** A stored date or clock time ("2023-09-05T13:00Z", "14:30") rather than words to search. */
const STORED_TIME = /^(\d{4}-\d{2}-\d{2}([T ][\d:.]+(Z|[+-]\d{2}:?\d{2})?)?|\d{1,2}:\d{2}(:\d{2})?)$/;

function humanizeKey(key: string): string {
  const s = key.replace(/[_-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function addResponses(
  texts: SearchText[],
  responses: Record<string, unknown> | null | undefined,
  entity: EntityInfo | undefined,
) {
  if (!responses) return;
  const orderTexts = (orders: unknown) => {
    if (!Array.isArray(orders)) return;
    for (const o of orders) addText(texts, "order", "Order", (o as { order?: unknown })?.order);
  };
  orderTexts(responses.orders);
  if (Array.isArray(responses.rounds)) {
    for (const r of responses.rounds) orderTexts((r as { orders?: unknown })?.orders);
  }
  if (Array.isArray(responses.questions)) {
    for (const q of responses.questions as { question?: unknown; answer?: unknown; correctAnswer?: unknown }[]) {
      addText(texts, "answer", "Trivia", q?.question);
      addText(texts, "answer", "Trivia answer", q?.answer);
      if (q?.correctAnswer !== q?.answer) addText(texts, "answer", "Trivia answer", q?.correctAnswer);
    }
  }
  const labels = new Map((entity?.customQuestions ?? []).map((q) => [q.id, q.label]));
  for (const [key, value] of Object.entries(responses)) {
    if (NON_TEXT_RESPONSE_KEYS.has(key) || typeof value !== "string") continue;
    if (STORED_TIME.test(value.trim())) continue;
    addText(texts, "answer", labels.get(key) ?? humanizeKey(key), value);
  }
}

/**
 * Turn a trip, its segments and its activities into search docs.
 *
 * `entities` resolves the places activities and segments point at; a place
 * not loaded yet just goes unnamed until it is.
 */
export function buildTripDocs(
  trip: TripLike,
  corpus: TripCorpus,
  entities: Record<string, EntityInfo>,
): SearchDoc[] {
  const docs: SearchDoc[] = [];
  const tripZone = trip.timezone ?? undefined;

  const tripTexts: SearchText[] = [];
  addText(tripTexts, "name", "Trip", trip.name);
  addText(tripTexts, "note", "Trip notes", trip.notes);
  for (const m of Object.values(trip.travelParty ?? {})) {
    addText(tripTexts, "party", "Travel party", m?.displayName);
  }
  addPeople(tripTexts, trip.people, TRIP_ROLE_TYPES);
  const tripDay = dayKeyOf(trip.startDate);
  docs.push({
    kind: "trip",
    id: trip.id,
    tripId: trip.id,
    title: trip.name,
    icon: "\u{1F9F3}",
    context: null,
    when: tripDay ? dateFromDayKey(tripDay) : null,
    zone: tripZone,
    href: `/trips/${trip.id}`,
    texts: tripTexts,
  });

  const segById = new Map(corpus.segments.map((s) => [s.id, s]));
  const onDay = segmentDayLookup(corpus.segments);
  const onZone = segmentZoneLookup(corpus.segments, tripZone);

  for (const s of corpus.segments) {
    const texts: SearchText[] = [];
    addText(texts, "name", s.dayNumber != null ? "Day" : "Segment", s.name);
    const ent = s.entityId ? entities[s.entityId] : undefined;
    if (ent?.displayName && ent.displayName !== s.name) {
      addText(texts, "place", ent.type ? entityTypeLabel(ent.type) : "Place", ent.displayName);
    }
    if (s.portName && s.portName !== s.name) addText(texts, "place", "Port", s.portName);
    addText(texts, "note", "Notes", s.notes);
    addPeople(texts, s.people, SEGMENT_ROLE_TYPES);
    const parent = s.parentSegmentId ? segById.get(s.parentSegmentId) : undefined;
    docs.push({
      kind: "segment",
      id: s.id,
      tripId: trip.id,
      title: s.name,
      icon: ent?.type ? entityTypeIcon(ent.type) : "\u{1F4C5}",
      context: parent?.name ?? null,
      when: onDay(s.id),
      zone: onZone(s.id),
      href: `/trips/${trip.id}/segments/${s.id}`,
      texts,
    });
  }

  for (const i of corpus.interactions) {
    const ent = i.entityId ? entities[i.entityId] : undefined;
    const type: EntityType | null =
      normalizeEntityType(ent?.type) ?? normalizeEntityType(i.customResponses?.entityType);
    const title = ent?.displayName || i.name || "Activity";
    const texts: SearchText[] = [];
    addText(texts, "name", type ? entityTypeLabel(type) : "Activity", title);
    if (i.name && ent?.displayName && i.name !== ent.displayName) addText(texts, "name", "Name", i.name);
    addText(texts, "note", "Notes", i.notes);
    for (const ev of i.events ?? []) {
      if (!ev?.notes) continue;
      addText(texts, "note", `${ev.customLabel || formatEventType(ev.eventType)} note`, ev.notes);
    }
    addPeople(texts, i.people, ACTIVITY_ROLE_TYPES);
    addResponses(texts, i.customResponses, ent);
    const seg = i.segmentId ? segById.get(i.segmentId) : undefined;
    docs.push({
      kind: "activity",
      id: i.id,
      tripId: trip.id,
      title,
      icon: entityTypeIcon(type),
      context: seg?.name ?? null,
      when: activityStart(i, onDay),
      zone: activityZone(i, onZone) ?? tripZone,
      href: `/trips/${trip.id}/interactions/${i.id}`,
      texts,
    });
  }

  return docs;
}
